import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import 'bootstrap-select';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingSpinner from 'component/LoadingSpinner';
import ProjectService from 'service/ProjectService';
import { ProjectTaskListResp } from 'domain/payload/response/project/ProjectTaskListResp';
import { ProjectDetailResp } from 'domain/payload/response/project/ProjectDetailResp';
import TabNavigation from 'component/TabNavigator';
import { ProjectJobListResp } from 'domain/payload/response/project/ProjectJobListResp';
import ProjectJobDetailModal from './ProjectJobDetailModal';
import { hasProjectAccess } from '../../helpers/hasPrivilege';
import ProjectAssignModal from './ProjectAssignModal';
import { ProjectJobGeoListResp } from 'domain/payload/response/project/ProjectJobGeoListResp';
import { ProjectJobGeoConfigResp, ProjectJobGeoMetricDataResp } from 'domain/payload/response/project/ProjectJobGeoConfigResp';
import { ProjectJobDetailResp } from 'domain/payload/response/project/ProjectJobDetailResp';
import Map, { Marker, Popup, MapRef } from 'react-map-gl/mapbox'
import { MAPBOX_TOKEN } from 'config/Mapbox'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import ProjectJobEditModal from './ProjectJobUpdateModal';
import { QRCodeCanvas } from 'qrcode.react';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

ChartJS.register(ArcElement, Tooltip, Legend);

const ProjectDetail: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const projectService = ProjectService();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [statusJob, setStatusJob] = useState("all")

  const [project, setProject] = useState<ProjectDetailResp | null>(null);
  const [tasks, setTasks] = useState<ProjectTaskListResp[]>([]);
  const [jobs, setJobs] = useState<ProjectJobListResp[]>([]);

  const [jobDetail, setJobDetail] = useState<ProjectJobDetailResp | null>(null)
  const [isLoadingJobDetail, setIsLoadingJobDetail] = useState(false)

  const [tabMode, setTabMode] = useState<string>('job');

  const [showModalJobDetail, setShowModalJobDetail] = useState(false);
  const [showModalAssign, setShowModalAssign] = useState(false);
  const [jobViewMode, setJobViewMode] = useState<"list" | "map">("list");
  const isMapMode = jobViewMode === "map"
  const isListMode = jobViewMode === "list"
  const [jobGeoList, setJobGeoList] = useState<ProjectJobGeoListResp[]>([]);
  const [jobGeoConfig, setJobGeoConfig] = useState<ProjectJobGeoConfigResp | null>(null);
  const [isLoadingGeo, setIsLoadingGeo] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ProjectJobGeoListResp | null>(null);
  const [selectedGeoStatuses, setSelectedGeoStatuses] = useState<string[]>([])
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const hasFitBounds = useRef(false)
  const mapRef = useRef<MapRef | null>(null)
  const [showModalJobEdit, setShowModalJobEdit] = useState(false);
  const [editJobId, setEditJobId] = useState<string | null>(null);
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedForm, setSelectedForm] = useState<{ name: string; demoCode: string; testingUrl: string } | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  const { navRef: jobViewModeNavRef, indicatorStyle: jobViewModeIndicatorStyle } = useNavPillsIndicator(jobViewMode);
  const { navRef: statusJobNavRef, indicatorStyle: statusJobIndicatorStyle } = useNavPillsIndicator(statusJob);

  const metricRuleMap = useMemo(() => {
    const map: Record<string, ProjectJobGeoMetricDataResp> = {}

    jobGeoConfig?.metricList[0]?.data.forEach(metric => {
      metric.rules.forEach(rule => {
        map[rule] = metric
      })
    })

    return map
  }, [jobGeoConfig])

  const fitBoundsToMarkers = useCallback((list: ProjectJobGeoListResp[]) => {
    if (!mapRef.current || list.length === 0) return

    const lngs = list.map(j => j.longitude)
    const lats = list.map(j => j.latitude)

    mapRef.current.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 80, duration: 800 }
    )
  }, [])

  const buildMarkerSvg = (color: string) => `
    <svg width="46" height="46" viewBox="0 0 46 46" xmlns="http://www.w3.org/2000/svg">
      <path d="M23.1436 45.757L3.13397 25.731L42.7225 25.3101L23.1436 45.757Z" fill="#${color}"/>
      <ellipse cx="22.8865" cy="20.4632" rx="20.4736" ry="20.4632" fill="#${color}"/>
      <ellipse cx="22.8865" cy="20.4632" rx="9.09937" ry="9.09476" fill="black" fill-opacity="0.2"/>
    </svg>
  `

  const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div className="mb-3">
      <div className="text-muted small">{label}</div>
      <div className="fw-semibold">{value || "-"}</div>
    </div>
  )

  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  // LOADING
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTask, setIsLoadingTask] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);

  const canCreate = hasProjectAccess(profile, 'create');
  const canUpdate = hasProjectAccess(profile, 'update');

  useEffect(() => {
    fetchProjectDetail();
  }, []);

  useEffect(() => {
    if (tabMode === 'task') {
      fetchDataTaskList(currentPage, pageSize);
    } else if (tabMode === 'job') {
      fetchDataJobList(currentPage, pageSize);
    }
  }, [currentPage, pageSize, statusJob, tabMode]);

  useEffect(() => {
    if (!mapRef.current || !jobGeoList.length) return
    if (hasFitBounds.current) return

    const lngs = jobGeoList.map(j => j.longitude)
    const lats = jobGeoList.map(j => j.latitude)

    mapRef.current.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 60, duration: 500 }
    )
    hasFitBounds.current = true
  }, [jobGeoList])

  useEffect(() => {
    if (tabMode === "job" && jobViewMode === "map") {
      fetchJobGeoConfig()
      fetchJobGeoList() // ALL
    }
  }, [jobViewMode, tabMode, searchTerm, statusJob])

  useEffect(() => {
    if (!selectedJobId) return

    fetchJobDetail(selectedJobId)
  }, [selectedJobId])

  useEffect(() => {
    setIsSidebarOpen(false)
    setSelectedJob(null)
    setSelectedJobId(null)
    setJobDetail(null)
  }, [jobViewMode, tabMode])

  useEffect(() => {
    if (jobGeoList.length === 0) return
    setTimeout(() => {
      fitBoundsToMarkers(jobGeoList)
    }, 100)
  }, [jobGeoList])

  useEffect(() => {
    if (tabMode === "job" && jobViewMode === "map" && jobGeoConfig?.metricList[0]?.data) {
      const allRules = jobGeoConfig.metricList[0].data.map(metric => metric.rules[0])
      setSelectedGeoStatuses(allRules)
    }
  }, [jobViewMode, tabMode, jobGeoConfig])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleCloseQRModal();
      }
    };

    if (showQRModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showQRModal]);

  const handleShowQR = (form: { name: string; demoCode: string; testingUrl: string }) => {
    if (!form.demoCode) {
      return;
    }
    setSelectedForm(form);
    setShowQRModal(true);
  };

  const handleCloseQRModal = () => {
    setShowQRModal(false)
    setSelectedForm(null)
  };

  const fetchProjectDetail = async () => {
    const token = profile?.data.token;
    if (!token || !id) {
      if (!token) window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await projectService.projectDetail(token, id);
      setProject(res.data!);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail project';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataTaskList = async (page: number, size: number) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingTask(true);
    try {
      const resTask = await projectService.projectTaskList(token, id!, null, null, page, size);
      setTasks(resTask.data || []);
      setTotalPages(resTask.pagination?.totalPage ?? 0);
      setTotalRecords(resTask.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data task';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingTask(false);
    }
  };

  const fetchDataJobList = async (page: number, size: number) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingTask(true);
    try {
      const resTask = await projectService.projectJobList(token, id!, statusJob, searchTerm, page, size);
      setJobs(resTask.data || []);
      setTotalPages(resTask.pagination?.totalPage ?? 0);
      setTotalRecords(resTask.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data job';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingTask(false);
    }
  };

  const fetchJobDetail = async (jobId: string) => {
    const token = profile?.data.token
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingJobDetail(true)
    try {
      const res = await projectService.projectJobDetail(token, jobId)
      setJobDetail(res.data ?? null)
    } catch (err: any) {
      console.error(err)
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail job';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingJobDetail(false)
    }
  }

  const fetchJobGeoConfig = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const res = await projectService.projectJobGeoConfig(token);
      setJobGeoConfig(Array.isArray(res.data) ? res.data[0] ?? null : res.data ?? null);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil konfigurasi geo';
      window.alert(`Error: ${errorMessage}`);
    }
  };

  const fetchJobGeoList = async () => {
    const token = profile?.data.token
    if (!token || !id) {
      if (!token) window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    setIsLoadingGeo(true)
    try {
      const res = await projectService.projectJobGeoList(
        token,
        id,
        statusJob,
        searchTerm
      )
      setJobGeoList(res.data?.filter(item => item.latitude != null && item.longitude != null && item.latitude !== 0 && item.longitude !== 0) ?? [])
    } catch (err: any) {
      console.error(err)
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data geo';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingGeo(false)
    }
  }

  const handleSearchJobClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setCurrentPage(1);
    if (jobViewMode === "list") {
      fetchDataJobList(1, pageSize);
    } else if (jobViewMode === "map") {
      fetchJobGeoList();
    }
  };

  const handleExportXlsx = async () => {
    const token = profile?.data.token;
    if (!token || !id) {
      if (!token) window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    try {
      setIsLoadingExport(true);
      const response = await projectService.projectJobExportXlsx(token, searchTerm, id, statusJob);
      window.alert(response?.message || 'File export sedang diproses dan akan dikirimkan ke email Anda.');
    } catch (err: any) {
      console.error("Export failed:", err);
      const errorMessage = err.response?.data?.message || err.message || err || 'Gagal melakukan export file';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingExport(false);
    }
  };

  const refreshData = () => {
    if (tabMode === 'job') {
      fetchDataJobList(currentPage, pageSize);
      if (jobViewMode === 'map') {
        fetchJobGeoList();
      }
    } else if (tabMode === 'task') {
      fetchDataTaskList(currentPage, pageSize);
    }
  };

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4 fw-bold">Project Detail</h4>
        <div className="p-4 bg-white mb-4 rounded shadow-sm">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <h4 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                    {project?.name}
                    {(project?.reportingDasboard || project?.reportingDashboard) && (
                      <span className="text-primary" data-bs-toggle="tooltip" title="Available on MoreSight" style={{ cursor: 'pointer' }}>
                        <i className="fa-solid fa-chart-pie" style={{ fontSize: '1rem' }}></i>
                      </span>
                    )}
                  </h4>
                  <div className="d-flex gap-3 text-muted small flex-wrap mt-2 align-items-center">
                    <div>
                      <span className="cluster-badge">
                        <i className="fa-solid fa-mountain cluster-badge-icon"></i>
                        {project?.cluster ? project?.cluster : 'Other'}
                      </span>
                    </div>
                    <span><i className="fa-regular fa-calendar-days me-1"></i> {project?.startDate} – {project?.endDate}</span>
                    <span><i className="fa-solid fa-file-invoice me-1"></i> {project?.instruction?.name || "No instruction"}</span>
                    <div>
                      <span className={`badge ${project?.isRunning ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
                        {project?.isRunning ? 'Running' : 'Stopped'}
                      </span>
                    </div>
                    <div>
                      <span className={`badge ${project?.showMapDetail !== false ? 'bg-info text-white' : 'bg-secondary'}`} style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
                        <i className={`fa-solid ${project?.showMapDetail !== false ? 'fa-map-location-dot' : 'fa-location-dot'} me-1`}></i>
                        Map Detail: {project?.showMapDetail !== false ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div>
                      <span className={`badge ${project?.geocodeResult ? 'bg-info text-white' : 'bg-secondary'}`} style={{ fontSize: '0.8rem', padding: '6px 10px' }} title="Automatically detect and resolve location addresses from survey result coordinates">
                        <i className={`fa-solid ${project?.geocodeResult ? 'fa-map-pin' : 'fa-location-dot'} me-1`}></i>
                        Geocode Result: {project?.geocodeResult ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
                {canUpdate && (
                  <Link to={`/project/${id}`}>
                    <button
                      className="btn btn-outline-secondary text-nowrap"
                      style={{ height: '50px', padding: '0 20px' }}
                    >
                      Edit Project <i className="fa-regular fa-pen-to-square ms-1" />
                    </button>
                  </Link>
                )}
              </div>

              <div className="row g-4 mb-4 align-items-stretch">
                {/* Left Side: Progress Meter Card */}
                <div className="col-12 col-lg-8 d-flex">
                  <div className="p-4 bg-light rounded-3 border w-100 d-flex flex-column justify-content-center">
                    <div className="row align-items-center">
                      <div className="col-6 col-md-3 border-end">
                        <div className="text-muted small">Target</div>
                        <h2 className="fw-bold mb-0 text-dark">{project?.target ?? 0}</h2>
                      </div>
                      <div className="col-6 col-md-3 border-end">
                        <div className="text-muted small">Total Result</div>
                        <h2 className="fw-bold mb-0 text-dark">{project?.resultCount?.total ?? 0}</h2>
                      </div>
                      <div className="col-12 col-md-6 mt-3 mt-md-0">
                        {(() => {
                          const target = project?.target ?? 0;
                          const approved = project?.resultCount?.approved ?? 0;
                          const percentage = target > 0 ? Math.round((approved / target) * 100) : 0;
                          return (
                            <div>
                              <div className="d-flex justify-content-between mb-1 small">
                                <span className="text-muted">Progress</span>
                                <span className="fw-bold text-primary">{percentage}%</span>
                              </div>
                              <div className="progress" style={{ height: '8px' }}>
                                <div
                                  className="progress-bar bg-primary"
                                  role="progressbar"
                                  style={{ width: `${Math.min(percentage, 100)}%` }}
                                  aria-valuenow={percentage}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                ></div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Side: Doughnut Breakdown Card */}
                <div className="col-12 col-lg-4 d-flex">
                  <div className="p-4 bg-light rounded-3 border w-100 d-flex align-items-center justify-content-center">
                    {(() => {
                      const approved = project?.resultCount?.approved ?? 0;
                      const rejected = project?.resultCount?.rejected ?? 0;
                      const done = project?.resultCount?.done ?? 0;
                      const archived = project?.resultCount?.archived ?? 0;
                      const total = project?.resultCount?.total ?? 0;

                      const chartData = {
                        labels: ['Approved', 'Rejected', 'Done', 'Archived'],
                        datasets: [
                          {
                            data: [approved, rejected, done, archived],
                            backgroundColor: ['#198754', '#dc3545', '#ffc107', '#6c757d'],
                            borderWidth: 0,
                            cutout: '75%',
                          },
                        ],
                      };

                      const chartOptions = {
                        plugins: {
                          legend: { display: false },
                          tooltip: { enabled: true }
                        },
                        maintainAspectRatio: false,
                        responsive: true
                      };

                      return (
                        <div className="d-flex align-items-center gap-4 w-100 justify-content-center">
                          <div style={{ width: '100px', height: '100px', position: 'relative', flexShrink: 0 }}>
                            <Doughnut data={chartData} options={chartOptions} />
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center'
                              }}
                            >
                              <span className="small text-muted" style={{ fontSize: '0.65rem', display: 'block', lineHeight: 1 }}>Total</span>
                              <strong className="fs-6 text-dark" style={{ lineHeight: 1.1 }}>{total}</strong>
                            </div>
                          </div>

                          <div style={{ fontSize: '0.8rem', minWidth: '120px' }}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="d-inline-block rounded-circle" style={{ width: '6px', height: '6px', background: '#198754' }}></span>
                              <span>Approved: <strong className="text-dark">{approved}</strong></span>
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="d-inline-block rounded-circle" style={{ width: '6px', height: '6px', background: '#ffc107' }}></span>
                              <span>Done: <strong className="text-dark">{done}</strong></span>
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="d-inline-block rounded-circle" style={{ width: '6px', height: '6px', background: '#dc3545' }}></span>
                              <span>Rejected: <strong className="text-dark">{rejected}</strong></span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="d-inline-block rounded-circle" style={{ width: '6px', height: '6px', background: '#6c757d' }}></span>
                              <span>Archived: <strong className="text-dark">{archived}</strong></span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className="border-bottom mb-4 pb-2 d-flex justify-content-start">
                <TabNavigation
                  activeTab={tabMode}
                  onTabChange={(tab) => {
                    setTabMode(tab);
                    setCurrentPage(1);
                  }}
                  tabs={[
                    { key: 'job', label: 'Job' },
                    { key: 'task', label: 'Task' },
                  ]}
                />
              </div>

              {tabMode === 'job' && (
                <>
                  <div className="mb-3 d-flex justify-content-between align-items-center flex-nowrap gap-2 overflow-auto">
                    <form className="d-flex align-items-center" onSubmit={handleSearchJobClick}>
                      <input
                        type="text"
                        className="form-control me-2"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ minWidth: '120px', height: '50px' }}
                      />
                      <button
                        type="submit"
                        className="btn btn-primary d-flex align-items-center justify-content-center"
                        style={{ height: '50px', width: '60px' }}
                      >
                        <i className="fa-solid fa-search"></i>
                      </button>
                    </form>

                    <div className="d-flex align-items-center">
                      {tabMode === "job" && (
                        <div ref={jobViewModeNavRef} className="nav-pills-animated h-100 mx-5" style={{ height: 50, maxHeight: 50 }}>
                          <div
                            className="nav-pill-indicator"
                            style={jobViewModeIndicatorStyle}
                          />
                          <button
                            type='button'
                            className={`nav-link h-100 ${jobViewMode === "list" ? "active" : ""}`}
                            style={{ padding: '12px 20px' }}
                            onClick={() => setJobViewMode("list")}
                          >
                            <i className="fa-solid fa-list me-1" />
                          </button>
                          <button
                            type='button'
                            className={`nav-link h-100 ${jobViewMode === "map" ? "active" : ""}`}
                            style={{ padding: '12px 20px' }}
                            onClick={() => setJobViewMode("map")}
                          >
                            <i className="fa-solid fa-map me-1" />
                          </button>
                        </div>
                      )}

                      <button
                        className="btn btn-outline-primary me-2"
                        style={{ height: '50px', padding: '0 20px' }}
                        onClick={handleExportXlsx}
                        disabled={isLoadingTask || isLoadingExport}
                      >
                        {isLoadingTask || isLoadingExport ? (
                          <span className="d-flex align-items-center">
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Exporting...
                          </span>
                        ) : (
                          "Export Data"
                        )}
                      </button>
                      {canCreate && (
                        <>
                          <Link to={`/project/detail/${id}/job-creation`} className="me-2">
                            <button
                              className="btn btn-outline-primary text-nowrap"
                              style={{ height: '50px', padding: '0 20px' }}
                            >
                              Job Creation
                            </button>
                          </Link>
                          <Link to={`/project/detail/${id}/advance-assignment`}>
                            <button
                              className="btn btn-primary text-nowrap"
                              style={{ height: '50px', padding: '0 20px' }}
                            >
                              Advance Assignment
                            </button>
                          </Link>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mb-3 d-flex justify-content-between align-items-center flex-nowrap gap-2 overflow-auto">
                    {jobViewMode === "map" && (
                      <>
                        <div className="d-flex gap-2 flex-nowrap mb-3">
                          {jobGeoConfig?.metricList[0].data.map(metric => {
                            const rule = metric.rules[0]
                            const active = selectedGeoStatuses.includes(rule)
                            const count = jobGeoList.filter(j =>
                              j.tags.includes(rule)
                            ).length
                            return (
                              <button
                                key={metric.id}
                                title={metric.name}
                                onClick={() => {
                                  setSelectedGeoStatuses(prev =>
                                    prev.includes(rule)
                                      ? prev.filter(r => r !== rule)
                                      : [...prev, rule]
                                  )
                                }}
                                className="px-3 py-1 rounded-pill fw-semibold"
                                style={{
                                  backgroundColor: active ? `#${metric.color}` : "#fff",
                                  color: active ? "#fff" : `#${metric.color}`,
                                  border: `1px solid #${metric.color}`,
                                }}
                              >
                                {count}
                              </button>
                            )
                          })}
                        </div>

                        <div className='mb-3'>
                          <div ref={statusJobNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                            <div
                              className="nav-pill-indicator"
                              style={statusJobIndicatorStyle}
                            />
                            <button
                              type='button'
                              className={`h-100 nav-link ${statusJob === "all" ? 'active' : ''}`}
                              onClick={() => {
                                setStatusJob("all");
                              }}
                            >
                              All
                            </button>
                            <button
                              type='button'
                              className={`h-100 nav-link ${statusJob === "outstanding" ? 'active' : ''}`}
                              onClick={() => {
                                setStatusJob("outstanding");
                              }}
                            >
                              Outstanding
                            </button>
                            <button
                              type='button'
                              className={`h-100 nav-link ${statusJob === "unassigned" ? 'active' : ''}`}
                              onClick={() => {
                                setStatusJob("unassigned");
                              }}
                            >
                              Unassigned
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {jobViewMode === "list" && isListMode && (
                      <div className='ms-auto'>
                        <div ref={statusJobNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                          <div
                            className="nav-pill-indicator"
                            style={statusJobIndicatorStyle}
                          />
                          <button
                            className={`h-100 nav-link ${statusJob === "all" ? 'active' : ''}`}
                            onClick={() => {
                              setStatusJob("all");
                              setCurrentPage(1);
                            }}
                          >
                            All
                          </button>
                          <button
                            className={`h-100 nav-link ${statusJob === "outstanding" ? 'active' : ''}`}
                            onClick={() => {
                              setStatusJob("outstanding");
                              setCurrentPage(1);
                            }}
                          >
                            Outstanding
                          </button>
                          <button
                            className={`h-100 nav-link ${statusJob === "unassigned" ? 'active' : ''}`}
                            onClick={() => {
                              setStatusJob("unassigned");
                              setCurrentPage(1);
                            }}
                          >
                            Unassigned
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {jobViewMode === "list" && isListMode && (
                    <>
                      <table className="table table-condensed table-hover">
                        <thead className="table-primary">
                          <tr>
                            <th>Job ID</th>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Form Name</th>
                            <th>Place Reference ID</th>
                            <th>Max Input</th>
                            <th>Current Assignment</th>
                            <th>Assignment Count</th>
                            <th>Result Count</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {!isLoadingTask ? (
                            jobs.length > 0 ? (
                              jobs.map((item) => (
                                <tr key={item.id}>
                                  <td>{item.id}</td>
                                  <td>{item.name}</td>
                                  <td>{item.description}</td>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <span>{item.formName}</span>
                                      {item.testingUrl && (
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary btn-small-compact border-0"
                                          title="Show QR Code"
                                          onClick={() => handleShowQR({
                                            name: item.formName,
                                            demoCode: item.demoCode,
                                            testingUrl: item.testingUrl,
                                          })}
                                        >
                                          <i className="fa-solid fa-qrcode"></i>
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td>{item.placeReferenceId}</td>
                                  <td>{item.maxInput}</td>
                                  <td>{item.workerNames.join(",")}</td>
                                  <td>
                                    <span className="fw-bold text-warning">{item.activeAssignmentCount ?? 0}</span>
                                    -
                                    <span className="fw-bold text-success">{item.completeAssignmentCount ?? 0}</span>
                                    -
                                    <span className="fw-bold text-danger">{item.failedAssignmentCount ?? 0}</span>
                                  </td>
                                  <td>
                                    <span className="fw-bold">{item.resultQcCount + item.resultApproveCount + item.resultRejectCount + item.resultLegacyCount}</span>
                                    -
                                    <span className="fw-bold text-warning">{item.resultQcCount}</span>
                                    -
                                    <span className="fw-bold text-success">{item.resultApproveCount}</span>
                                    -
                                    <span className="fw-bold text-danger">{item.resultRejectCount}</span>
                                    -
                                    <span className="fw-bold text-primary">{item.resultLegacyCount}</span>
                                    -
                                    <span className="fw-bold text-secondary">{item.resultArchivedCount}</span>
                                  </td>
                                  <td>
                                    <button className="btn btn-outline-info btn-small-compact border-0"
                                      onClick={() => {
                                        setSelectedItem(item);
                                        setShowModalJobDetail(true);
                                      }}>
                                      <i className="fa-solid fa-eye"></i>
                                    </button>
                                    {canCreate && (
                                      <button className="btn btn-outline-warning btn-small-compact border-0"
                                        onClick={() => {
                                          setSelectedItem({
                                            ...item,
                                            projectId: id,
                                            projectName: project?.name || '-',
                                          });
                                          setShowModalAssign(true);
                                        }}>
                                        <i className="fa-solid fa-user-pen"></i>
                                      </button>
                                    )}
                                    {canUpdate && (
                                      <button className="btn btn-outline-secondary btn-small-compact border-0"
                                        onClick={() => {
                                          setEditJobId(item.id);
                                          setShowModalJobEdit(true);
                                        }}>
                                        <i className="fa-solid fa-pen-to-square"></i>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={10} className="text-center text-muted py-3">
                                  Tidak ada data
                                </td>
                              </tr>
                            )
                          ) : (
                            <tr>
                              <td colSpan={10} className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      <div className="d-flex justify-content-end align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          <div className="me-4 d-flex align-items-center">
                            <label className="me-2">Rows per page:</label>
                            <CustomDropdown
                              value={pageSize}
                              options={PageSizeOption.options}
                              onChange={(opt) => {
                                setPageSize(Number(opt.id));
                                setCurrentPage(1);
                              }}
                              align="top"
                            />
                          </div>

                          <span className="me-3">
                            {totalRecords > 0
                              ? `${(currentPage - 1) * pageSize + 1}–${Math.min(
                                currentPage * pageSize,
                                totalRecords
                              )} of ${totalRecords}`
                              : "0 data"}
                          </span>

                          <nav>
                            <ul className="pagination mb-0">
                              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(1)}
                                  style={{ border: 0 }}
                                >
                                  <i className="fa fa-angle-double-left"></i>
                                </button>
                              </li>
                              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                  style={{ border: 0 }}
                                >
                                  <i className="fa fa-angle-left"></i>
                                </button>
                              </li>
                              <li className="page-item disabled">
                                <span className="page-link" style={{ border: 0 }}>
                                  {currentPage}
                                </span>
                              </li>
                              <li
                                className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() =>
                                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                                  }
                                  style={{ border: 0 }}
                                >
                                  <i className="fa fa-angle-right"></i>
                                </button>
                              </li>
                              <li
                                className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(totalPages)}
                                  style={{ border: 0 }}
                                >
                                  <i className="fa fa-angle-double-right"></i>
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    </>
                  )}

                  {jobViewMode === "map" && isMapMode && (
                    <div className='d-flex' style={{ height: "600px" }}>
                      <div className="flex-grow-1" style={{ position: 'relative' }}>
                        {isLoadingGeo ? (
                          <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                            <p className="mt-2">Loading map...</p>
                          </div>
                        ) : (
                          <Map
                            ref={mapRef}
                            mapboxAccessToken={MAPBOX_TOKEN}
                            initialViewState={{
                              longitude: 106.816666,
                              latitude: -6.200000,
                              zoom: 12,
                            }}
                            style={{ width: '100%', height: '100%' }}
                            mapStyle="mapbox://styles/mapbox/streets-v12"
                            onLoad={() => {
                              if (jobGeoList.length > 0) {
                                fitBoundsToMarkers(jobGeoList)
                              }
                            }}
                          >
                            {jobGeoList
                              .filter(job => {
                                const tag = job.tags[0]
                                return selectedGeoStatuses.length === 0 || selectedGeoStatuses.includes(tag)
                              })
                              .map((job) => {
                                const tag = job.tags[0] ?? 'data'
                                const metric = metricRuleMap[tag]
                                const color = metric?.color ?? '888888'
                                const svgUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildMarkerSvg(color))}`
                                const isSelected = selectedJob?.id === job.id

                                return (
                                  <Marker
                                    key={job.id}
                                    longitude={job.longitude}
                                    latitude={job.latitude}
                                    anchor="bottom"
                                    onClick={(e: any) => {
                                      e.originalEvent.stopPropagation()
                                      setSelectedJob(job)
                                      setSelectedItem(job)
                                      setSelectedJobId(job.id)
                                      setIsSidebarOpen(true)
                                    }}
                                  >
                                    <img
                                      src={svgUrl}
                                      style={{
                                        width: isSelected ? 38 : 28,
                                        height: isSelected ? 38 : 28,
                                        cursor: 'pointer',
                                        transition: 'width 0.15s, height 0.15s',
                                        filter: isSelected ? 'drop-shadow(0 0 4px rgba(0,0,0,0.5))' : 'none'
                                      }}
                                      alt={job.tags[0]}
                                    />
                                  </Marker>
                                )
                              })
                            }
                          </Map>
                        )}
                      </div>

                      {isSidebarOpen && selectedJob && (
                        <div
                          className="border-start bg-white shadow-sm d-flex flex-column"
                          style={{ width: 380, height: "100%" }}
                        >
                          <div className="px-3 py-3 border-bottom d-flex justify-content-between align-items-center">
                            <strong>Job Detail</strong>

                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-light btn-sm"
                                title="Close"
                                onClick={() => {
                                  setIsSidebarOpen(false)
                                  setSelectedJob(null)
                                  setSelectedJobId(null)
                                  setJobDetail(null)
                                }}
                              >
                                <i className="fa-solid fa-xmark text-muted" />
                              </button>
                            </div>
                          </div>


                          <div className="px-3 py-2 border-bottom d-flex gap-1 align-items-center">
                            {canCreate && (
                              <button
                                type='button'
                                className="btn btn-outline-warning btn-small-compact border-0"
                                onClick={() => {
                                  setSelectedItem({
                                    ...selectedJob,
                                    projectId: id,
                                    description: jobDetail?.description || '-',
                                    projectName: project?.name || '-',
                                    maxInput: jobDetail?.maxInput ?? 0,
                                  })
                                  setShowModalAssign(true)
                                }}
                              >
                                <i
                                  className="fa-solid fa-user-pen"
                                  title="Assign Worker"
                                />
                              </button>
                            )}
                            {canUpdate && selectedJob && (
                              <button
                                type='button'
                                className="btn btn-outline-secondary btn-small-compact border-0"
                                onClick={() => {
                                  setEditJobId(selectedJob.id);
                                  setShowModalJobEdit(true);
                                }}
                              >
                                <i
                                  className="fa-solid fa-pen-to-square"
                                  title="Edit Job"
                                />
                              </button>
                            )}
                          </div>

                          <div className="flex-grow-1 overflow-auto p-3">
                            {!jobDetail ? (
                              <div className="text-center py-5">
                                <div className="spinner-border spinner-border-sm" />
                              </div>
                            ) : (
                              <>
                                <InfoRow label="Job ID" value={jobDetail.id} />
                                <InfoRow label="Name" value={jobDetail.name} />
                                <InfoRow label="Description" value={jobDetail.description} />
                                <InfoRow label="Place Reference ID" value={jobDetail.placeReferenceId} />
                                <InfoRow label="Max Input" value={jobDetail.maxInput} />

                                <hr />

                                <InfoRow
                                  label="Current Assignment"
                                  value={jobDetail.currentWorkerName?.join(",") || "-"}
                                />

                                <InfoRow
                                  label="Assignment Count"
                                  value={
                                    <>
                                      <span className="fw-bold text-warning">
                                        {jobDetail.activeAssignmentCount}
                                      </span>{" "}
                                      -{" "}
                                      <span className="fw-bold text-success">
                                        {jobDetail.completeAssignmentCount}
                                      </span>{" "}
                                      -{" "}
                                      <span className="fw-bold text-danger">
                                        {jobDetail.failedAssignmentCount}
                                      </span>
                                    </>
                                  }
                                />

                                <InfoRow
                                  label="Result Count"
                                  value={
                                    <>
                                      <span className="fw-bold">
                                        {jobDetail.resultQcCount +
                                          jobDetail.resultApproveCount +
                                          jobDetail.resultRejectCount}
                                      </span>{" "}
                                      -{" "}
                                      <span className="fw-bold text-warning">
                                        {jobDetail.resultQcCount}
                                      </span>{" "}
                                      -{" "}
                                      <span className="fw-bold text-success">
                                        {jobDetail.resultApproveCount}
                                      </span>{" "}
                                      -{" "}
                                      <span className="fw-bold text-danger">
                                        {jobDetail.resultRejectCount}
                                      </span>
                                    </>
                                  }
                                />
                              </>
                            )}
                          </div>
                        </div>

                      )}

                    </div>
                  )}
                </>
              )}

              {tabMode === 'task' && (
                <div className="text-muted">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div />
                    <div>
                      {canCreate && (
                        <button className="btn btn-outline-primary me-2">Import Plan From CSV</button>
                      )}
                      {canCreate && (
                        <Link to={`/project/assignment/${id}`}>
                          <button className="btn btn-primary">New Task <i className="fa fa-plus ms-1" /></button>
                        </Link>
                      )}
                    </div>
                  </div>

                  <table className="table table-condensed table-hover">
                    <thead className="table-primary">
                      <tr>
                        <th>Name</th>
                        <th>Store</th>
                        <th>Installer</th>
                        <th>Created By</th>
                        <th>Created At</th>
                        <th>Status</th>
                        <th>Goods</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!isLoadingTask ? (
                        tasks.length > 0 ? (
                          tasks.map((task) => (
                            <tr key={task.id}>
                              <td>{task.name}</td>
                              <td>{task.storeName}</td>
                              <td>{task.installerName}</td>
                              <td>{task.createdBy}</td>
                              <td>{task.createdAt}</td>
                              <td>{task.status}</td>
                              <td>{task.goodsCounts} goods</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={8} className="text-center">
                              Tidak ada data
                            </td>
                          </tr>
                        )
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className="d-flex justify-content-end align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      <div className="me-4 d-flex align-items-center">
                        <label className="me-2">Rows per page:</label>
                        <CustomDropdown
                          value={pageSize}
                          options={PageSizeOption.options}
                          onChange={(opt) => {
                            setPageSize(Number(opt.id));
                            setCurrentPage(1);
                          }}
                          align="top"
                        />
                      </div>

                      <span className="me-3">
                        {totalRecords > 0
                          ? `${(currentPage - 1) * pageSize + 1}–${Math.min(
                            currentPage * pageSize,
                            totalRecords
                          )} of ${totalRecords}`
                          : "0 data"}
                      </span>

                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(1)}
                              style={{ border: 0 }}
                            >
                              <i className="fa fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                              style={{ border: 0 }}
                            >
                              <i className="fa fa-angle-left"></i>
                            </button>
                          </li>
                          <li className="page-item disabled">
                            <span className="page-link" style={{ border: 0 }}>
                              {currentPage}
                            </span>
                          </li>
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() =>
                                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                              }
                              style={{ border: 0 }}
                            >
                              <i className="fa fa-angle-right"></i>
                            </button>
                          </li>
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(totalPages)}
                              style={{ border: 0 }}
                            >
                              <i className="fa fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              )}

              {showModalJobDetail && selectedItem && (
                <ProjectJobDetailModal
                  item={selectedItem}
                  onClose={() => setShowModalJobDetail(false)}
                />
              )}
              {showModalAssign && selectedItem && (
                <ProjectAssignModal
                  item={selectedItem}
                  onClose={(isRefresh) => {
                    setShowModalAssign(false);
                    if (isRefresh) {
                      refreshData();
                    }
                  }}
                />
              )}
              {showModalJobEdit && editJobId && (
                <ProjectJobEditModal
                  jobId={editJobId}
                  onClose={(isRefresh) => {
                    setShowModalJobEdit(false);
                    setEditJobId(null);
                    if (isRefresh) {
                      refreshData();
                      if (selectedJobId) {
                        fetchJobDetail(selectedJobId);
                      }
                    }
                  }}
                />
              )}
              {showQRModal && selectedForm && (
                <div
                  className="modal show d-block"
                  tabIndex={-1}
                  role="dialog"
                  style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}
                  onClick={handleCloseQRModal}
                >
                  <div
                    className="modal-dialog modal-dialog-centered"
                    role="document"
                    ref={modalRef}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="modal-content">
                      <div className="text-center p-4">
                        <h5 className="mb-4">{selectedForm.name}</h5>
                        <div className="d-flex justify-content-center p-4 rounded">
                          <QRCodeCanvas
                            id="qr-code-canvas"
                            value={selectedForm.demoCode}
                            size={200}
                            level="H"
                          />
                        </div>
                        <p className="fw-bold mb-4">Code: {selectedForm.demoCode}</p>
                        <p className="fw-bold mb-2">Web Form Test</p>
                        <a
                          href={selectedForm.testingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fw-bold mb-2 text-primary"
                          style={{
                            wordBreak: 'break-all',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                        >
                          {selectedForm.testingUrl}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
